import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"
import DOMPurify from "dompurify"
import { marked } from "marked"
import SparkMD5 from "spark-md5"

// Configure marked for chat-style rendering (compact, no extra line breaks)
marked.setOptions({
  breaks: false,  // Don't convert \n to <br> - causes excessive spacing in chat
  gfm: true
})

// Count user-perceived characters (grapheme clusters) so the counter matches
// what iOS's Swift String.count and Rails's GraphemeLengthValidator see. JS
// `String.length` counts UTF-16 code units — "👨‍👩‍👧‍👦" is 11 units but 1
// grapheme — which would cause the web counter to disagree with both client
// and server for any emoji ZWJ sequence.
//
// Intl.Segmenter (widely supported) does it correctly; fallback to code points
// via Array.from() which is at least closer than code units.
const GRAPHEME_SEGMENTER =
  typeof Intl !== "undefined" && typeof Intl.Segmenter === "function"
    ? new Intl.Segmenter(undefined, { granularity: "grapheme" })
    : null

function graphemeCount(str) {
  if (!str) return 0
  if (GRAPHEME_SEGMENTER) {
    let n = 0
    for (const _ of GRAPHEME_SEGMENTER.segment(str)) n += 1
    return n
  }
  return Array.from(str).length
}

export default class extends Controller {
  static targets = [
    "messages", "typing", "form", "input", "sendButton", "error",
    "quotaDisplay", "loadMoreIndicator", "allHistoryLoaded",
    "imagePreviews", "fileInput", "dropzone", "lightbox", "lightboxImage",
    "charCounter", "emptyChat", "toastContainer"
  ]
  static values = {
    roomId: String,
    familyRoom: Boolean,
    streamUrl: String,
    interactUrlTemplate: String,
    messagesUrl: String,
    disclaimerTitle: String,
    disclaimerContent: String,
    disclaimerAcceptLabel: String,
    maxContentLength: Number,
    maxImages: { type: Number, default: 4 },
    directUploadUrl: String,
    imageCreateUrl: String,
    quotaRemaining: Number,
    quotaLimit: Number,
    hasMore: Boolean,
    nextCursor: String,
    // i18n error messages
    errorNetwork: String,
    errorStreamInterrupted: String,
    errorMessageTooLong: String,
    errorReconnecting: String,
    errorReconnectFailed: String,
    contentIncomplete: String,
    allHistoryLoaded: String,
    babySwitched: String,
    retryUpload: String,
    maxImagesMessage: String,
    quotaWarningOne: String,
    quotaWarningOther: String,
    quotaExhausted: String,
    quotaExceededTitle: String,
    quotaUpgradeButton: String
  }

  connect() {
    this.isStreaming = false
    this.currentStreamMessage = null
    this.isLoadingMore = false
    this.pendingImages = [] // { tempId, file, status, progress, previewUrl, imageId }
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 3
    this.scrollToBottom()

    // Focus input on connect
    if (this.hasInputTarget) {
      this.inputTarget.focus()
      this.updateCharCounter()
    }

    // Set up scroll listener for pagination
    if (this.hasMessagesTarget) {
      this.messagesTarget.addEventListener("scroll", this.handleScroll.bind(this))
    }

    // Set up drag and drop
    this.setupDragDrop()

    // Set up paste handling
    this.setupPaste()

    // Show all history loaded if no more messages
    this.updateAllHistoryLoadedState()
  }

  disconnect() {
    // Clean up any active stream
    if (this.abortController) {
      this.abortController.abort()
    }
    // Clean up preview URLs
    this.pendingImages.forEach(img => {
      if (img.previewUrl) {
        URL.revokeObjectURL(img.previewUrl)
      }
    })
  }

  // ==================== Character Counter ====================

  updateCharCounter() {
    if (!this.hasCharCounterTarget || !this.hasInputTarget) return

    const current = graphemeCount(this.inputTarget.value)
    const max = this.maxContentLengthValue
    const warningThreshold = Math.floor(max * 0.9) // 90%

    this.charCounterTarget.textContent = `${current}/${max}`

    // Update classes based on length
    this.charCounterTarget.classList.remove("warning", "error")
    if (current > max) {
      this.charCounterTarget.classList.add("error")
    } else if (current >= warningThreshold) {
      this.charCounterTarget.classList.add("warning")
    }
  }

  // ==================== Image Upload Methods ====================

  openFilePicker() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  handleFileSelect(event) {
    const files = Array.from(event.target.files)
    this.addImages(files)
    // Reset input so the same file can be selected again
    event.target.value = ""
  }

  setupDragDrop() {
    const container = this.element
    let dragCounter = 0

    container.addEventListener("dragenter", (e) => {
      e.preventDefault()
      dragCounter++
      if (this.hasDropzoneTarget) {
        this.dropzoneTarget.classList.remove("hidden")
      }
    })

    container.addEventListener("dragleave", (e) => {
      e.preventDefault()
      dragCounter--
      if (dragCounter === 0 && this.hasDropzoneTarget) {
        this.dropzoneTarget.classList.add("hidden")
      }
    })

    container.addEventListener("dragover", (e) => {
      e.preventDefault()
    })

    container.addEventListener("drop", (e) => {
      e.preventDefault()
      dragCounter = 0
      if (this.hasDropzoneTarget) {
        this.dropzoneTarget.classList.add("hidden")
      }
      this.handleDrop(e)
    })
  }

  handleDrop(event) {
    const files = Array.from(event.dataTransfer.files).filter(f => f.type.startsWith("image/"))
    if (files.length > 0) {
      this.addImages(files)
    }
  }

  setupPaste() {
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("paste", this.handlePaste.bind(this))
    }
  }

  handlePaste(event) {
    // Prevent paste when input is disabled
    if (this.hasInputTarget && this.inputTarget.disabled) {
      event.preventDefault()
      return
    }

    const items = Array.from(event.clipboardData.items)
    const imageItems = items.filter(item => item.type.startsWith("image/"))

    if (imageItems.length > 0) {
      event.preventDefault()
      const files = imageItems.map(item => item.getAsFile()).filter(Boolean)
      this.addImages(files)
    }
  }

  addImages(files) {
    const maxImages = this.maxImagesValue
    const currentCount = this.pendingImages.filter(i => i.status !== "failed").length
    const remainingSlots = maxImages - currentCount

    if (remainingSlots <= 0) {
      this.showError(this.maxImagesMessageValue || `Maximum ${maxImages} images allowed`)
      return
    }

    const filesToAdd = files.slice(0, remainingSlots)

    filesToAdd.forEach(file => {
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      const previewUrl = URL.createObjectURL(file)

      const imageData = {
        tempId,
        file,
        status: "uploading",
        progress: 0,
        previewUrl,
        imageId: null
      }

      this.pendingImages.push(imageData)
      this.uploadImage(imageData)
    })

    this.renderPreviews()
    this.updateSendButtonState()
  }

  async uploadImage(imageData) {
    try {
      // Step 1: Calculate MD5 checksum
      const checksum = await this.calculateChecksum(imageData.file)

      // Step 2: Get presigned upload URL from Rails
      const presignResponse = await fetch(this.directUploadUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        },
        body: JSON.stringify({
          blob: {
            filename: imageData.file.name,
            byte_size: imageData.file.size,
            checksum: checksum,
            content_type: imageData.file.type
          }
        })
      })

      if (!presignResponse.ok) {
        throw new Error("Failed to get upload URL")
      }

      const presignData = await presignResponse.json()
      const { signed_id, direct_upload } = presignData

      // Step 3: Upload file directly to storage
      await this.uploadToStorage(imageData, direct_upload.url, direct_upload.headers)

      // Step 4: Create Image record
      const imageResponse = await fetch(this.imageCreateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        },
        body: JSON.stringify({
          signed_id: signed_id
        })
      })

      if (!imageResponse.ok) {
        throw new Error("Failed to create image record")
      }

      const imageRecord = await imageResponse.json()

      // Update status
      imageData.status = "ready"
      imageData.imageId = imageRecord.id
      imageData.progress = 100

      this.renderPreviews()
      this.updateSendButtonState()
    } catch (error) {
      console.error("Image upload failed:", error)
      imageData.status = "failed"
      this.renderPreviews()
      this.updateSendButtonState()
    }
  }

  async uploadToStorage(imageData, url, headers) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          imageData.progress = Math.round((e.loaded / e.total) * 100)
          this.renderPreviews()
        }
      })

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`))
        }
      })

      xhr.addEventListener("error", () => reject(new Error("Upload failed")))
      xhr.addEventListener("abort", () => reject(new Error("Upload aborted")))

      xhr.open("PUT", url)

      // Set headers from presigned response
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value)
      })

      xhr.send(imageData.file)
    })
  }

  calculateChecksum(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const spark = new SparkMD5.ArrayBuffer()
        spark.append(e.target.result)
        const hash = spark.end(true) // Raw binary
        // Convert to base64
        const base64 = btoa(hash)
        resolve(base64)
      }
      reader.onerror = () => reject(new Error("Failed to read file"))
      reader.readAsArrayBuffer(file)
    })
  }

  renderPreviews() {
    if (!this.hasImagePreviewsTarget) return

    const retryTitle = this.retryUploadValue || "Click to retry"

    const html = this.pendingImages.map(img => {
      const statusClass = img.status === "uploading" ? "uploading" :
                          img.status === "failed" ? "failed" : "ready"

      const retryAction = img.status === "failed"
        ? `data-action="click->chat-room#retryImage"`
        : ""

      return `
        <div class="chat-preview-item ${statusClass}" data-temp-id="${img.tempId}" ${retryAction}>
          <img src="${img.previewUrl}" alt="">
          ${img.status === "uploading" ? this.getProgressRing(img.progress) : ""}
          ${img.status === "failed" ? `<div class="preview-error" title="${retryTitle}"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg></div>` : ""}
          <button type="button" class="preview-remove" data-action="click->chat-room#removeImage" data-temp-id="${img.tempId}">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
            </svg>
          </button>
        </div>
      `
    }).join("")

    this.imagePreviewsTarget.innerHTML = html
  }

  getProgressRing(progress) {
    const circumference = 2 * Math.PI * 18 // radius = 18
    const offset = circumference - (progress / 100) * circumference
    return `
      <div class="progress-ring-container">
        <svg class="progress-ring" width="44" height="44">
          <circle class="progress-ring-bg" cx="22" cy="22" r="18" fill="none" stroke-width="4"/>
          <circle class="progress-ring-bar" cx="22" cy="22" r="18" fill="none" stroke-width="4"
                  stroke-dasharray="${circumference}" stroke-dashoffset="${offset}"/>
        </svg>
      </div>
    `
  }

  retryImage(event) {
    const previewItem = event.currentTarget.closest(".chat-preview-item")
    if (!previewItem) return

    const tempId = previewItem.dataset.tempId
    const imageData = this.pendingImages.find(img => img.tempId === tempId)

    if (imageData && imageData.status === "failed") {
      // Reset status and retry upload
      imageData.status = "uploading"
      imageData.progress = 0
      this.renderPreviews()
      this.uploadImage(imageData)
    }
  }

  removeImage(event) {
    event.preventDefault()
    event.stopPropagation()

    const tempId = event.currentTarget.dataset.tempId
    const index = this.pendingImages.findIndex(img => img.tempId === tempId)

    if (index !== -1) {
      const img = this.pendingImages[index]
      if (img.previewUrl) {
        URL.revokeObjectURL(img.previewUrl)
      }
      this.pendingImages.splice(index, 1)
      this.renderPreviews()
      this.updateSendButtonState()
    }
  }

  updateSendButtonState() {
    if (!this.hasSendButtonTarget) return

    const hasContent = this.hasInputTarget && this.inputTarget.value.trim().length > 0
    const hasReadyImages = this.pendingImages.some(img => img.status === "ready")
    const hasUploadingImages = this.pendingImages.some(img => img.status === "uploading")

    // Disable send if:
    // - Uploading in progress
    // - Neither content nor images are ready
    // - Streaming is in progress
    const shouldDisable = this.isStreaming || hasUploadingImages || (!hasContent && !hasReadyImages)
    this.sendButtonTarget.disabled = shouldDisable
  }

  // ==================== Lightbox Methods ====================

  openLightbox(event) {
    const target = event.currentTarget
    const largeUrl = target.dataset.largeUrl

    if (largeUrl && this.hasLightboxTarget && this.hasLightboxImageTarget) {
      this.lightboxImageTarget.src = largeUrl
      this.lightboxTarget.classList.remove("hidden")
      document.body.style.overflow = "hidden"
    }
  }

  closeLightbox(event) {
    // Don't close if clicking on the image itself
    if (event && event.target === this.lightboxImageTarget) {
      return
    }

    if (this.hasLightboxTarget) {
      this.lightboxTarget.classList.add("hidden")
      document.body.style.overflow = ""
    }
  }

  // ==================== Quick Suggestions ====================

  useSuggestion(event) {
    const suggestion = event.currentTarget.dataset.suggestion
    if (suggestion && this.hasInputTarget) {
      this.inputTarget.value = suggestion
      this.inputTarget.focus()
      this.autoResize()
      this.updateCharCounter()
    }
  }

  // ==================== Message Sending ====================

  // Handle form submission
  sendMessage(event) {
    event.preventDefault()

    const content = this.inputTarget.value.trim()
    const readyImages = this.pendingImages.filter(img => img.status === "ready")
    const imageIds = readyImages.map(img => img.imageId)

    // Must have content or images
    if (!content && imageIds.length === 0) return
    if (this.isStreaming) return

    // Check for uploading images
    if (this.pendingImages.some(img => img.status === "uploading")) {
      this.showError("Please wait for images to finish uploading")
      return
    }

    if (content && !this.validateContentLength(content)) {
      return
    }

    // Generate idempotency key for this message
    const clientMessageId = crypto.randomUUID()

    // Family room: use Turbo form submission instead of SSE
    if (this.familyRoomValue) {
      this.sendFamilyMessage(content, imageIds, clientMessageId)
    } else {
      this.startStream(content, imageIds, clientMessageId)
    }
  }

  // Send message in family room (no AI, no streaming)
  async sendFamilyMessage(content, imageIds, clientMessageId) {
    this.isStreaming = true
    this.setLoading(true)
    this.clearError()

    // Clear input and previews
    this.inputTarget.value = ""
    this.autoResize()

    // Hide empty chat if visible
    if (this.hasEmptyChatTarget) {
      this.emptyChatTarget.classList.add("hidden")
    }

    // Clear pending images
    this.pendingImages.forEach(img => {
      if (img.previewUrl) {
        URL.revokeObjectURL(img.previewUrl)
      }
    })
    this.pendingImages = []
    this.renderPreviews()

    try {
      const formData = new FormData()
      formData.append("content", content)
      formData.append("client_message_id", clientMessageId)
      imageIds.forEach(id => formData.append("image_ids[]", id))

      const response = await fetch(`/chat/${this.roomIdValue}/messages`, {
        method: "POST",
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.getCsrfToken()
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const html = await response.text()
      Turbo.renderStreamMessage(html)
      this.scrollToBottom()
    } catch (error) {
      console.error("Failed to send family message:", error)
      this.showError(this.getErrorMessage("network_error"))
      // Restore content
      this.inputTarget.value = content
      this.autoResize()
    } finally {
      this.isStreaming = false
      this.setLoading(false)
      this.updateSendButtonState()
    }
  }

  // Handle keyboard shortcuts
  handleKeydown(event) {
    // Enter without shift sends the message
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage(event)
    }
    // Shift+Enter adds a new line (default behavior)

    // Update send button state on each keydown
    this.updateSendButtonState()
  }

  // Auto-resize textarea
  autoResize() {
    const textarea = this.inputTarget
    textarea.style.height = "auto"
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px"
    this.updateSendButtonState()
    this.updateCharCounter()
  }

  // Start SSE stream
  async startStream(content, imageIds = [], clientMessageId = null) {
    this.isStreaming = true
    this.setLoading(true)
    this.clearError()
    this.lastMessageContent = content
    this.lastMessageImageIds = imageIds
    this.lastClientMessageId = clientMessageId || crypto.randomUUID()
    this.reconnectAttempts = 0

    // Clear input and previews
    const originalContent = this.inputTarget.value
    this.inputTarget.value = ""
    this.autoResize()

    // Hide empty chat if visible
    if (this.hasEmptyChatTarget) {
      this.emptyChatTarget.classList.add("hidden")
    }

    // Clear pending images on successful send
    this.pendingImages.forEach(img => {
      if (img.previewUrl) {
        URL.revokeObjectURL(img.previewUrl)
      }
    })
    this.pendingImages = []
    this.renderPreviews()

    await this.executeStream(content, imageIds, this.lastClientMessageId, originalContent)
  }

  async executeStream(content, imageIds, clientMessageId, originalContent) {
    // Create abort controller for this request
    this.abortController = new AbortController()

    try {
      const bodyParams = new URLSearchParams()
      bodyParams.append("content", content)
      bodyParams.append("client_message_id", clientMessageId)
      imageIds.forEach(id => bodyParams.append("image_ids[]", id))

      const response = await fetch(this.streamUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "text/event-stream",
          "X-CSRF-Token": this.getCsrfToken()
        },
        body: bodyParams,
        signal: this.abortController.signal
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      await this.processStream(reader, decoder)

      // Reset reconnect attempts on successful completion
      this.reconnectAttempts = 0
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Stream aborted")
        return
      }

      console.error("Stream error:", error)

      // Try to reconnect
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++
        const delay = Math.pow(2, this.reconnectAttempts - 1) * 1000 // 1s, 2s, 4s
        this.showReconnectingStatus()

        await new Promise(resolve => setTimeout(resolve, delay))

        // Only retry if still streaming (not aborted)
        if (this.isStreaming) {
          await this.executeStream(content, imageIds, clientMessageId, originalContent)
          return
        }
      } else {
        this.showError(this.getErrorMessage("reconnect_failed"))

        // Restore content on final error
        this.inputTarget.value = originalContent
        this.autoResize()
      }
    } finally {
      // Handle incomplete stream - render any accumulated content
      this.finalizeIncompleteStream()

      this.isStreaming = false
      this.setLoading(false)
      this.abortController = null
      this.updateSendButtonState()
    }
  }

  showReconnectingStatus() {
    if (this.hasErrorTarget) {
      this.errorTarget.innerHTML = `
        <div class="chat-reconnecting">
          <div class="loading-spinner small"></div>
          <span>${this.errorReconnectingValue || "Reconnecting..."}</span>
        </div>
      `
    }
  }

  // Process the SSE stream
  async processStream(reader, decoder) {
    let buffer = ""
    const STREAM_TIMEOUT = 60000 // 60 seconds timeout for no data

    while (true) {
      let timeoutId
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error("Stream timeout")), STREAM_TIMEOUT)
      })

      let result
      try {
        // Race between read and timeout
        result = await Promise.race([
          reader.read(),
          timeoutPromise
        ])
      } catch (error) {
        console.warn("Stream timeout or error:", error.message)
        break
      } finally {
        clearTimeout(timeoutId)
      }

      const { done, value } = result

      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // Process complete events from buffer
      const lines = buffer.split("\n")
      buffer = lines.pop() || "" // Keep incomplete line in buffer

      let currentEvent = null
      let currentData = ""

      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEvent = line.slice(7)
        } else if (line.startsWith("data: ")) {
          currentData = line.slice(6)

          if (currentEvent && currentData) {
            try {
              const data = JSON.parse(currentData)
              this.handleSSEEvent(currentEvent, data)
            } catch (e) {
              console.error("Failed to parse SSE data:", e)
            }
          }

          currentEvent = null
          currentData = ""
        }
      }
    }
  }

  // Handle individual SSE events
  handleSSEEvent(eventType, data) {
    switch (eventType) {
      case "user_message":
        this.appendUserMessage(data)
        break

      case "message_start":
        this.startAssistantMessage(data)
        break

      case "content_delta":
        this.appendContentDelta(data)
        break

      case "message_end":
        this.finishAssistantMessage(data)
        break

      case "error":
        this.handleStreamError(data)
        break

      default:
        console.log("Unknown SSE event:", eventType, data)
    }
  }

  // Append user message to chat
  appendUserMessage(data) {
    if (data.html) {
      this.messagesTarget.insertAdjacentHTML("beforeend", data.html)
      this.scrollToBottom()
    }
  }

  // Start a new assistant message
  startAssistantMessage(data) {
    this.showTypingIndicator()
    this.currentStreamMessage = null
    this.streamingContent = ""
  }

  // Append content delta to current assistant message
  appendContentDelta(data) {
    if (!this.currentStreamMessage) {
      // Create the message element
      const messageHtml = this.createStreamingMessageHtml()
      this.messagesTarget.insertAdjacentHTML("beforeend", messageHtml)
      this.currentStreamMessage = this.messagesTarget.lastElementChild
      this.hideTypingIndicator()
    }

    // Append content (server sends "delta" field)
    const textElement = this.currentStreamMessage.querySelector(".message-text")
    const content = data.delta || data.content
    if (textElement && content) {
      // Accumulate raw content for final markdown rendering
      this.streamingContent += content
      // Show escaped text while streaming (will be replaced with rendered markdown on finish)
      const escapedContent = this.escapeHtml(content)
      textElement.innerHTML += escapedContent.replace(/\n/g, "<br>")
      this.scrollToBottom()
    }
  }

  // Finish the assistant message
  finishAssistantMessage(data) {
    this.hideTypingIndicator()

    if (this.currentStreamMessage) {
      // Remove streaming class
      this.currentStreamMessage.classList.remove("streaming")

      // Render accumulated content as Markdown
      const textElement = this.currentStreamMessage.querySelector(".message-text")
      if (textElement && this.streamingContent) {
        textElement.innerHTML = DOMPurify.sanitize(marked.parse(this.streamingContent))
      }

      // If we have the final HTML from server, use it instead
      if (data.html) {
        const wrapper = document.createElement("div")
        wrapper.innerHTML = data.html
        const newMessage = wrapper.firstElementChild
        if (newMessage) {
          this.currentStreamMessage.replaceWith(newMessage)
        }
      }

      // Update time
      const timeElement = this.currentStreamMessage.querySelector(".message-time")
      if (timeElement && data.time) {
        timeElement.textContent = data.time
      }
    }

    // Update debug banner with model info
    if (data.model || data.provider) {
      this.updateDebugBanner(data.model, data.provider)
    }

    // Sync quota display if server returned quota info
    if (data.quota !== undefined) {
      this.quotaRemainingValue = data.quota.remaining
      this.quotaLimitValue = data.quota.limit
      this.updateQuotaDisplay()
    }

    this.currentStreamMessage = null
    this.streamingContent = ""
    this.scrollToBottom()
  }

  // Handle incomplete stream (connection dropped before message_end)
  finalizeIncompleteStream() {
    this.hideTypingIndicator()

    // If we have an incomplete streaming message, finalize it
    if (this.currentStreamMessage && this.streamingContent) {
      console.log("Finalizing incomplete stream with accumulated content")

      // Remove streaming class
      this.currentStreamMessage.classList.remove("streaming")

      // Render accumulated content as Markdown
      const textElement = this.currentStreamMessage.querySelector(".message-text")
      if (textElement) {
        textElement.innerHTML = DOMPurify.sanitize(marked.parse(this.streamingContent))

        // Add incomplete notice
        const incompleteNotice = this.contentIncompleteValue || "(Connection lost, content may be incomplete)"
        textElement.innerHTML += `<p class="content-incomplete-notice">${this.escapeHtml(incompleteNotice)}</p>`
      }

      // Update time to now
      const timeElement = this.currentStreamMessage.querySelector(".message-time")
      if (timeElement) {
        const now = new Date()
        timeElement.textContent = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
      }

      this.scrollToBottom()
    }

    // Clean up state
    this.currentStreamMessage = null
    this.streamingContent = ""
  }

  // Handle stream error
  handleStreamError(data) {
    this.hideTypingIndicator()

    if (data.code === "disclaimer_not_accepted") {
      this.showDisclaimerModal()
    } else if (data.code === "quota_exceeded") {
      this.showQuotaExceeded()
    } else {
      this.showError(data.message || this.getErrorMessage("stream_interrupted"))
    }
  }

  // Handle option selection (for rich content)
  selectOption(event) {
    const button = event.currentTarget
    const value = button.dataset.value
    const label = button.dataset.label || value
    const selectBlock = button.closest(".block-select")
    const selectId = selectBlock?.dataset.selectId

    if (!value) return

    // Disable all options in this block
    selectBlock.querySelectorAll(".select-option").forEach(opt => {
      opt.disabled = true
    })

    // Highlight selected option
    button.classList.add("selected")

    // Handle baby_selector specially - call interact API
    if (selectId === "baby_selector") {
      this.handleBabySelection(button, value)
      return
    }

    // For other selects, send the label as a message
    this.inputTarget.value = label
    this.sendMessage(new Event("submit"))
  }

  // Handle baby selection via interact API
  async handleBabySelection(button, babyId) {
    const message = button.closest(".message")
    const messageId = message?.dataset.messageId

    if (!messageId) {
      console.error("No message ID found for baby selection")
      return
    }

    try {
      // Get interact URL from template or construct it
      const interactUrl = this.hasInteractUrlTemplateValue
        ? this.interactUrlTemplateValue.replace(":id", messageId)
        : `/chat/rooms/${this.roomIdValue}/messages/${messageId}/interact`

      const response = await fetch(interactUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        },
        body: JSON.stringify({
          action_id: "baby_selector",
          value: babyId
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      // Show toast with baby name
      const babyName = button.querySelector(".option-label")?.textContent || "Baby"
      this.showToast(this.babySwitchedValue?.replace("%{name}", babyName) || `Switched to ${babyName}`)

      // Check if there's an original question to resend
      const originalQuestion = data.rich_content?.original_question
      if (originalQuestion) {
        // Auto-resend the original question
        this.startStream(originalQuestion)
      }
    } catch (error) {
      console.error("Failed to handle baby selection:", error)
      this.showError(this.getErrorMessage("network_error"))
    }
  }

  // ==================== Toast Notifications ====================

  showToast(message, duration = 3000) {
    if (!this.hasToastContainerTarget) return

    const toast = document.createElement("div")
    toast.className = "toast"
    toast.textContent = message

    this.toastContainerTarget.appendChild(toast)

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add("show")
    })

    // Remove after duration
    setTimeout(() => {
      toast.classList.remove("show")
      setTimeout(() => toast.remove(), 300)
    }, duration)
  }

  // UI helpers
  showTypingIndicator() {
    if (this.hasTypingTarget) {
      this.typingTarget.classList.remove("hidden")
      this.scrollToBottom()
    }
  }

  hideTypingIndicator() {
    if (this.hasTypingTarget) {
      this.typingTarget.classList.add("hidden")
    }
  }

  setLoading(loading) {
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = loading
      if (loading) {
        this.sendButtonTarget.classList.add("sending")
        this.sendButtonTarget.innerHTML = this.getSpinnerSvg()
      } else {
        this.sendButtonTarget.classList.remove("sending")
        this.sendButtonTarget.innerHTML = this.getSendSvg()
      }
    }

    if (this.hasInputTarget) {
      this.inputTarget.disabled = loading
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.innerHTML = `
        <div class="chat-error-message">
          ${this.escapeHtml(message)}
          <button class="btn-retry" data-action="click->chat-room#retryLastMessage">
            ${this.getRetrySvg()} Retry
          </button>
        </div>
      `
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.innerHTML = ""
    }
  }

  showDisclaimerModal() {
    const existing = document.getElementById("disclaimer-modal")
    if (existing) {
      existing.classList.remove("hidden")
      return
    }

    const title = this.disclaimerTitleValue || "Terms of Use"
    const content = this.disclaimerContentValue || ""
    const acceptLabel = this.disclaimerAcceptLabelValue || "I understand, start using"

    const modal = document.createElement("div")
    modal.className = "modal"
    modal.id = "disclaimer-modal"
    modal.innerHTML = `
      <div class="modal-backdrop" data-action="click->chat-room#closeDisclaimerModal"></div>
      <div class="modal-content">
        <div class="disclaimer-content">
          <h3>${this.escapeHtml(title)}</h3>
          <div class="disclaimer-text">${this.escapeHtml(content)}</div>
          <div class="disclaimer-actions">
            <button class="btn-primary" data-action="click->chat-room#acceptDisclaimer">
              ${this.escapeHtml(acceptLabel)}
            </button>
          </div>
        </div>
      </div>
    `
    document.body.appendChild(modal)
  }

  closeDisclaimerModal() {
    const modal = document.getElementById("disclaimer-modal")
    if (modal) {
      modal.remove()
    }
  }

  async acceptDisclaimer() {
    try {
      const response = await fetch("/chat/accept_disclaimer", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      })

      if (response.ok || response.redirected) {
        this.closeDisclaimerModal()
        // Reload page to refresh state
        window.location.reload()
      }
    } catch (error) {
      console.error("Failed to accept disclaimer:", error)
    }
  }

  showQuotaExceeded() {
    if (this.hasErrorTarget) {
      const title = this.quotaExceededTitleValue || "Quota Exceeded"
      const message = this.quotaExhaustedValue || "Monthly chat limit reached. Upgrade to Pro for more conversations."
      const buttonText = this.quotaUpgradeButtonValue || "Upgrade Now"
      this.errorTarget.innerHTML = `
        <div class="quota-exceeded-content">
          <h3>${this.escapeHtml(title)}</h3>
          <p>${this.escapeHtml(message)}</p>
          <a href="/subscription" class="btn-primary">${this.escapeHtml(buttonText)}</a>
        </div>
      `
    }

    // Disable input
    if (this.hasInputTarget) {
      this.inputTarget.disabled = true
    }
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true
    }
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      // Use requestAnimationFrame for smooth scrolling
      requestAnimationFrame(() => {
        this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
      })
    }
  }

  retryLastMessage() {
    this.clearError()
    if (!this.lastMessageContent && (!this.lastMessageImageIds || this.lastMessageImageIds.length === 0)) return
    if (this.isStreaming) return

    if (this.lastMessageContent && !this.validateContentLength(this.lastMessageContent)) {
      return
    }

    // Reuse the same client message ID for idempotency
    this.startStream(this.lastMessageContent || "", this.lastMessageImageIds || [], this.lastClientMessageId)
  }

  validateContentLength(content) {
    if (!this.hasMaxContentLengthValue) return true

    if (graphemeCount(content) > this.maxContentLengthValue) {
      this.showError(this.getErrorMessage("message_too_long"))
      return false
    }

    return true
  }

  // Helper methods
  getCsrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }

  getErrorMessage(key) {
    // Use i18n values from data attributes
    const messages = {
      network_error: this.errorNetworkValue || "Network error. Please check your connection and try again.",
      stream_interrupted: this.errorStreamInterruptedValue || "Connection interrupted. Please try again.",
      message_too_long: this.errorMessageTooLongValue || "Message is too long. Please shorten it and try again.",
      reconnect_failed: this.errorReconnectFailedValue || "Reconnection failed. Please retry."
    }
    return messages[key] || "An error occurred. Please try again."
  }

  createStreamingMessageHtml() {
    return `
      <div class="message assistant streaming">
        <div class="message-avatar">
          <div class="ai-avatar">AI</div>
        </div>
        <div class="message-content">
          <div class="message-text markdown-body"></div>
          <div class="message-meta">
            <span class="message-time">--:--</span>
          </div>
        </div>
      </div>
    `
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  getSendSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>`
  }

  getSpinnerSvg() {
    return `<svg class="spinner" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" opacity="0.25"></circle><path d="M12 2a10 10 0 0 1 10 10" opacity="1"></path></svg>`
  }

  getRetrySvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>`
  }

  // Pagination: Handle scroll to load more messages
  handleScroll() {
    if (this.isLoadingMore || !this.hasMoreValue) return

    const container = this.messagesTarget
    // Trigger load when scrolled near the top (within 100px)
    if (container.scrollTop < 100) {
      this.loadMoreMessages()
    }
  }

  // Load more (older) messages
  async loadMoreMessages() {
    if (this.isLoadingMore || !this.hasMoreValue || !this.nextCursorValue) return

    this.isLoadingMore = true
    this.showLoadMoreIndicator()

    // Remember scroll position to maintain it after loading
    const container = this.messagesTarget
    const previousScrollHeight = container.scrollHeight

    try {
      const url = new URL(this.messagesUrlValue, window.location.origin)
      url.searchParams.set("before", this.nextCursorValue)

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      // Prepend older messages
      if (data.messages_html) {
        const indicator = this.hasLoadMoreIndicatorTarget ? this.loadMoreIndicatorTarget : null
        const insertPoint = indicator ? indicator.nextSibling : container.firstChild

        const wrapper = document.createElement("div")
        wrapper.innerHTML = data.messages_html

        // Insert each message after the load indicator
        while (wrapper.firstChild) {
          container.insertBefore(wrapper.firstChild, insertPoint)
        }
      }

      // Update pagination state
      this.hasMoreValue = data.has_more || false
      this.nextCursorValue = data.next_cursor || ""

      // Update all history loaded state
      this.updateAllHistoryLoadedState()

      // Maintain scroll position
      const newScrollHeight = container.scrollHeight
      container.scrollTop = newScrollHeight - previousScrollHeight
    } catch (error) {
      console.error("Failed to load more messages:", error)
    } finally {
      this.isLoadingMore = false
      this.hideLoadMoreIndicator()
    }
  }

  showLoadMoreIndicator() {
    if (this.hasLoadMoreIndicatorTarget) {
      this.loadMoreIndicatorTarget.classList.remove("hidden")
    }
  }

  hideLoadMoreIndicator() {
    if (this.hasLoadMoreIndicatorTarget) {
      this.loadMoreIndicatorTarget.classList.add("hidden")
    }
  }

  updateAllHistoryLoadedState() {
    if (this.hasAllHistoryLoadedTarget) {
      if (!this.hasMoreValue && this.nextCursorValue) {
        // Has loaded some messages and no more to load
        this.allHistoryLoadedTarget.classList.remove("hidden")
      } else {
        this.allHistoryLoadedTarget.classList.add("hidden")
      }
    }
  }

  // Update quota display after message completion
  updateQuotaDisplay() {
    if (!this.hasQuotaDisplayTarget) return

    const remaining = this.quotaRemainingValue
    const limit = this.quotaLimitValue

    // Update data attributes
    this.quotaDisplayTarget.dataset.remaining = remaining
    this.quotaDisplayTarget.dataset.limit = limit

    // Update display based on remaining quota
    if (remaining === 0) {
      this.quotaDisplayTarget.innerHTML = `
        <div class="quota-exhausted">
          ${this.escapeHtml(this.getQuotaExhaustedMessage())}
        </div>
      `
      // Disable input
      if (this.hasInputTarget) {
        this.inputTarget.disabled = true
      }
      if (this.hasSendButtonTarget) {
        this.sendButtonTarget.disabled = true
      }
    } else if (remaining <= 3) {
      this.quotaDisplayTarget.innerHTML = `
        <div class="quota-warning">
          ${this.escapeHtml(this.getQuotaWarningMessage(remaining))}
        </div>
      `
    } else {
      // Clear warning if quota is healthy
      this.quotaDisplayTarget.innerHTML = ""
    }
  }

  getQuotaWarningMessage(count) {
    if (count === 1) {
      return this.quotaWarningOneValue || `Only 1 message remaining this month`
    }
    const template = this.quotaWarningOtherValue || `Only %{count} messages remaining this month`
    return template.replace('%{count}', count)
  }

  getQuotaExhaustedMessage() {
    return this.quotaExhaustedValue || "Monthly chat limit reached"
  }

  // Update debug banner with model info
  updateDebugBanner(model, provider) {
    const banner = document.querySelector(".debug-model-banner")
    if (!banner) return

    const modelValue = banner.querySelector(".debug-value")
    const providerValue = banner.querySelectorAll(".debug-value")[1]

    if (modelValue && model) {
      modelValue.textContent = model
    }
    if (providerValue && provider) {
      // Capitalize first letter for display
      providerValue.textContent = provider.charAt(0).toUpperCase() + provider.slice(1)
    }
  }
}
